import { useCallback, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ChatDocumentDTO } from '@shared/schemas/chat';
import {
  Artifact,
  ArtifactClose,
  ArtifactContent,
  ArtifactHeader,
  ArtifactTitle,
} from '@/components/ai-elements/artifact';
import { getChatErrorMessage } from '../chat-errors';
import { CanvasLoadError, CanvasPlaceholder } from '../document-canvas/CanvasPlaceholder';
import { getChatAttachment } from '../services/chat-attachment.api';

// Les URL présignées expirent : on en redemande une avant leur fin de validité.
const DOWNLOAD_URL_STALE_MS = 5 * 60_000;

type ChatAttachmentPanelProps = {
  attachmentId: string;
  document: ChatDocumentDTO;
  onClose: () => void;
  /** Panneau du document-fichier, alimenté par l'URL de téléchargement résolue. */
  renderFileDocument: (document: ChatDocumentDTO, refreshSource: () => Promise<string | null>) => ReactNode;
};

/**
 * Ouvre une pièce jointe du chat dans le lecteur : l'URL présignée du fichier est demandée à
 * l'API (testing 2026-10-08 : « Impossible de charger le document source », pdf.js recevait
 * `attachment://…` et n'émettait aucune requête).
 */
export function ChatAttachmentPanel({ attachmentId, document, onClose, renderFileDocument }: ChatAttachmentPanelProps) {
  const attachmentQuery = useQuery({
    queryFn: () => getChatAttachment(attachmentId),
    queryKey: ['chat-attachment', attachmentId],
    staleTime: DOWNLOAD_URL_STALE_MS,
  });
  const { refetch } = attachmentQuery;
  const downloadUrl = attachmentQuery.data?.downloadUrl ?? null;
  const refreshSource = useCallback(async () => (await refetch()).data?.downloadUrl ?? null, [refetch]);
  const fileDocument = useMemo<ChatDocumentDTO | null>(() => (
    downloadUrl ? { ...document, sourceUrl: downloadUrl } : null
  ), [document, downloadUrl]);

  if (fileDocument) {
    return renderFileDocument(fileDocument, refreshSource);
  }

  const failureMessage = attachmentQuery.isError
    ? getChatErrorMessage(attachmentQuery.error)
    : attachmentQuery.isSuccess ? 'Le fichier source de cette pièce jointe n’est plus disponible.' : null;

  return (
    <Artifact className="h-full rounded-none border-none bg-sidebar shadow-none">
      <ArtifactHeader className="h-[3.25rem] border-border/50 bg-muted/20 px-4">
        <ArtifactTitle className="truncate text-sm font-semibold">{document.title}</ArtifactTitle>
        <ArtifactClose aria-label="Fermer le panneau" onClick={onClose} />
      </ArtifactHeader>
      <ArtifactContent className="flex-1 overflow-hidden p-0">
        {failureMessage
          ? <CanvasLoadError message={failureMessage} onRetry={() => void refetch()} />
          : <CanvasPlaceholder label="Chargement de la pièce jointe…" />}
      </ArtifactContent>
    </Artifact>
  );
}
